import { useCallback, useEffect, useState } from "react";
import { Badge, IconButton } from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { ACCESS_TOKEN } from "../../constants/AppVarConstants";
import { ROUTE_NOTIFICATIONS } from "../../constants/routes";
import { fetchUnreadNotificationCount } from "../../utils/notificationsApi";
import { initNotificationStore } from "../../utils/notificationStore";
import { FCM_FOREGROUND_EVENT } from "../../utils/fcmForegroundEvents";
import { NOTIFICATIONS_UPDATED_EVENT } from "../../utils/notificationsApi";

type NotificationNavButtonProps = {
  iconColor?: string;
};

const UNREAD_POLL_MS = 60_000;

const NotificationNavButton = ({ iconColor = "#fff" }: NotificationNavButtonProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [apiUnread, setApiUnread] = useState(0);

  const refreshApiUnread = useCallback(async () => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!token) {
      setApiUnread(0);
      return;
    }
    try {
      const count = await fetchUnreadNotificationCount();
      setApiUnread(count);
    } catch (err) {
      console.warn("[notifications] Failed to fetch unread count:", err);
    }
  }, []);

  useEffect(() => {
    initNotificationStore();
    refreshApiUnread();

    const onRefresh = () => refreshApiUnread();
    window.addEventListener(FCM_FOREGROUND_EVENT, onRefresh);
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, onRefresh);
    const pollId = window.setInterval(refreshApiUnread, UNREAD_POLL_MS);

    return () => {
      window.removeEventListener(FCM_FOREGROUND_EVENT, onRefresh);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, onRefresh);
      window.clearInterval(pollId);
    };
  }, [refreshApiUnread]);

  useEffect(() => {
    refreshApiUnread();
  }, [location.pathname, refreshApiUnread]);

  return (
    <IconButton
      aria-label="Notifications"
      onClick={() => navigate(ROUTE_NOTIFICATIONS)}
      sx={{ color: iconColor, p: 0.5 }}
    >
      <Badge badgeContent={apiUnread} color="error" max={99} invisible={apiUnread === 0}>
        <NotificationsNoneOutlinedIcon />
      </Badge>
    </IconButton>
  );
};

export default NotificationNavButton;
